import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAskProvider } from '../hooks/useAskProvider';
import { getCanvasMessages } from '../utils/canvasI18n';
import { AskProviderStartIcon } from './AskProviderIcons';

const GoogleIcon = ({ size = 14 }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    sx={{ width: size, height: size, display: 'block' }}
  >
    <path
      fill="#EA4335"
      d="M12 10.2v3.9h5.5c-.2 1.2-1.4 3.5-5.5 3.5-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.2 14.7 2.2 12 2.2c-5.4 0-9.8 4.4-9.8 9.8s4.4 9.8 9.8 9.8c5.7 0 9.5-4 9.5-9.6 0-.6-.1-1.1-.2-1.6H12z"
    />
    <path
      fill="#34A853"
      d="M2.2 7.8l3.2 2.3c.9-2.7 3.4-4.5 6.6-4.5 1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.2 14.7 2.2 12 2.2c-3.8 0-7.2 2.2-8.8 5.6z"
    />
    <path
      fill="#FBBC05"
      d="M12 21.8c2.6 0 4.8-.9 6.4-2.5l-3-2.5c-.8.6-2 1.1-3.4 1.1-3.1 0-5.7-2.1-6.6-4.9l-3.1 2.4c1.6 3.5 5.1 6.4 9.7 6.4z"
    />
    <path
      fill="#4285F4"
      d="M21.5 12.2c0-.6-.1-1.1-.2-1.6H12v3.9h5.5c-.3 1.2-1 2.1-2 2.8l3 2.5c1.7-1.6 2.9-3.9 2.9-7.6z"
    />
  </Box>
);

function needsDestination(kind) {
  return kind === 'search' || kind === 'ask';
}

function optionKey(option, index) {
  const kind = String(option?.kind || '').trim() || 'option';
  const label = String(option?.label || '').trim();
  return `${index}:${kind}:${label}`;
}

/**
 * Centered modal for interactive “Do next” jobs (decide / pick current goal).
 * Decide options are choices — click records the decision. Set-goal may still
 * ask for a search/ask destination.
 */
const BoardPlanModal = ({
  open,
  planAction,
  planCustomAnswer,
  onCustomAnswerChange,
  onApply,
  onClose,
  loading = false,
  resolvePlanActionKind,
  formatPlanOptionLabel,
  kindMeta = {},
  messages: messagesProp,
}) => {
  const [selectedOptionKey, setSelectedOptionKey] = useState(null);
  const [destination, setDestination] = useState(null);
  const { askProvider, askProviderLabel } = useAskProvider();
  const destinationRef = useRef(null);

  useEffect(() => {
    setSelectedOptionKey(null);
    setDestination(null);
  }, [planAction]);

  const messages = messagesProp || getCanvasMessages('en');
  const isSetGoal = planAction?.kind === 'set-goal';
  const title = isSetGoal
    ? messages.pickNextStep
    : (messages.decidePlan || messages.decide || messages.quickPlan);
  const customTrimmed = String(planCustomAnswer || '').trim();
  const hasCustomAnswer = Boolean(customTrimmed);

  const selectedOption = useMemo(() => {
    if (!selectedOptionKey || !planAction?.options?.length) return null;
    return planAction.options.find((option, index) => (
      optionKey(option, index) === selectedOptionKey
    )) || null;
  }, [planAction, selectedOptionKey]);

  // Destination UI follows the active path: selected chip, or custom text.
  const activeKind = selectedOption
    ? resolvePlanActionKind(selectedOption)
    : (hasCustomAnswer
      ? resolvePlanActionKind(null, {
        hasCustomAnswer: true,
        actionKind: planAction?.kind || '',
      })
      : '');

  const requiresDestination = isSetGoal
    && Boolean(activeKind)
    && needsDestination(activeKind);
  // Apply only for typed custom answers (chips / destinations run themselves).
  const canApplyCustom = hasCustomAnswer && !selectedOption && !loading;

  const applyLabel = isSetGoal
    ? ((activeKind && kindMeta[activeKind]?.prefix)
      || kindMeta.apply?.prefix
      || messages.apply
      || 'Apply')
    : (messages.decidePlan || messages.decide || 'Decide');

  useEffect(() => {
    if (!requiresDestination || !destinationRef.current) return;
    destinationRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [requiresDestination, selectedOptionKey, customTrimmed]);

  if (!planAction) return null;

  const runOption = (option, dest = null) => {
    if (loading || !option) return;
    onApply?.({
      option,
      answer: option.label,
      destination: dest,
    });
  };

  const handleOptionClick = (option, index) => {
    if (loading) return;
    onCustomAnswerChange?.('');
    const kind = resolvePlanActionKind(option);
    if (isSetGoal && needsDestination(kind)) {
      setSelectedOptionKey(optionKey(option, index));
      setDestination(null);
      return;
    }
    setSelectedOptionKey(optionKey(option, index));
    runOption(option, null);
  };

  const handleDestinationClick = (dest) => {
    if (loading || !dest) return;
    setDestination(dest);
    // Chip path: destination click runs immediately.
    if (selectedOption) {
      runOption(selectedOption, dest);
    }
    // Custom path: destination only enables Apply — do not auto-run.
  };

  const handleCustomChange = (value) => {
    setSelectedOptionKey(null);
    const trimmed = String(value || '').trim();
    // Custom answers are recorded as decisions — no search/ask destination.
    if (!trimmed) {
      setDestination(null);
    } else if (isSetGoal) {
      const kind = resolvePlanActionKind(null, {
        hasCustomAnswer: true,
        actionKind: planAction?.kind || '',
      });
      setDestination(prev => (
        needsDestination(kind) ? (prev || 'in-app') : null
      ));
    } else {
      setDestination(null);
    }
    onCustomAnswerChange?.(value);
  };

  const handleApplyCustom = () => {
    if (!canApplyCustom) return;
    const dest = requiresDestination ? (destination || 'in-app') : null;
    onApply?.({
      option: null,
      answer: customTrimmed,
      destination: dest,
    });
  };

  const inAppLabel = activeKind === 'search'
    ? messages.searchInApp
    : messages.askInApp;
  const externalLabel = activeKind === 'search'
    ? messages.searchGoogle
    : messages.askOuterProvider(askProviderLabel);
  const ExternalIcon = activeKind === 'search'
    ? GoogleIcon
    : (props) => <AskProviderStartIcon provider={askProvider} size={14} {...props} />;

  const showDestination = Boolean(
    isSetGoal && (
      (selectedOption && needsDestination(resolvePlanActionKind(selectedOption)))
      || (hasCustomAnswer && !selectedOption && needsDestination(activeKind))
    ),
  );

  const handleDialogClose = (_event, reason) => {
    if (loading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      className="board-ai-plan-dialog"
      PaperProps={{ className: 'board-ai-plan-paper' }}
      aria-labelledby="board-ai-plan-title"
    >
      <DialogTitle id="board-ai-plan-title" className="board-ai-plan-dialog-header">
        <Box>
          <Typography variant="caption" className="board-ai-plan-title">
            {title}
          </Typography>
          <Typography variant="body2" className="board-ai-plan-question">
            {planAction.question || planAction.label}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={messages.closePlan} size="small" disabled={loading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="board-ai-plan-dialog-body" dividers>
        {planAction.why ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {planAction.why}
          </Typography>
        ) : null}

        {isSetGoal ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {messages.setGoalHint}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" className="board-ai-plan-step-hint">
            {messages.planDecideHint || messages.planChooseHint}
          </Typography>
        )}

        {planAction.options?.length ? (
          <Box className="board-ai-plan-options">
            {planAction.options.map((option, index) => {
              const kind = isSetGoal ? resolvePlanActionKind(option) : 'apply';
              const chipLabel = isSetGoal
                ? formatPlanOptionLabel(option)
                : String(option?.label || '').trim();
              const key = optionKey(option, index);
              const selected = selectedOptionKey === key;
              const chipNeedsDest = isSetGoal && needsDestination(kind);
              return (
                <Chip
                  key={key}
                  label={chipLabel}
                  clickable
                  color="primary"
                  variant={selected ? 'filled' : 'outlined'}
                  disabled={loading}
                  title={chipNeedsDest
                    ? `${messages.chooseDestinationShort || messages.chooseDestination}: ${option.label}`
                    : `${messages.clickToContinue || messages.selectThenApply}: ${option.label}`}
                  onClick={() => handleOptionClick(option, index)}
                  className={[
                    'board-ai-plan-option-chip',
                    `board-ai-plan-option-chip--${kind}`,
                    selected ? 'is-selected' : '',
                  ].filter(Boolean).join(' ')}
                />
              );
            })}
          </Box>
        ) : null}

        {showDestination ? (
          <Box
            ref={destinationRef}
            className={[
              'board-ai-plan-destination',
              !destination ? 'is-awaiting' : '',
            ].filter(Boolean).join(' ')}
          >
            <Typography variant="caption" className="board-ai-plan-step-label" display="block">
              {messages.chooseDestination}
            </Typography>
            <Box className="board-ai-plan-destination-buttons">
              <Button
                size="small"
                variant={destination === 'in-app' ? 'contained' : 'outlined'}
                disabled={loading}
                onClick={() => handleDestinationClick('in-app')}
              >
                {inAppLabel}
              </Button>
              <Button
                size="small"
                variant={destination === 'external' ? 'contained' : 'outlined'}
                disabled={loading}
                startIcon={ExternalIcon ? <ExternalIcon size={12} /> : undefined}
                onClick={() => handleDestinationClick('external')}
              >
                {externalLabel}
              </Button>
            </Box>
          </Box>
        ) : null}

        <Box className="board-ai-plan-custom">
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder={messages.customAnswerPlaceholder}
            value={planCustomAnswer}
            onChange={e => handleCustomChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey && canApplyCustom) {
                e.preventDefault();
                handleApplyCustom();
              }
            }}
            inputProps={{ maxLength: 200, 'aria-label': 'Custom plan answer' }}
          />
          {hasCustomAnswer && !selectedOption ? (
            <Box className="board-ai-plan-dialog-actions">
              <Button
                size="small"
                variant="contained"
                disabled={!canApplyCustom}
                title={messages.applyDecisionTitle || messages.applyCustomTitle}
                onClick={handleApplyCustom}
                endIcon={canApplyCustom ? <ArrowForwardIcon fontSize="small" /> : undefined}
              >
                {applyLabel}
              </Button>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BoardPlanModal;
